import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Clock, UserPlus } from "lucide-react";
import { DayPills } from "./DayPills";
import { FrequencyPicker } from "./FrequencyPicker";
import { EmployeeMultiSelect } from "./EmployeeMultiSelect";
import { endFromStart, fmtH, weekInterventions, freqLabel, isDateDriven } from "../lib/planning";

const NEW_TYPE = "__new__";
const selectCls = "mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50 disabled:text-slate-500";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-700";

/** Assegnazione rapida: uno o più dipendenti + cantiere + tipologia + giorni + orario + ore → attività/turno/assegnazioni. */
export function QuickAssignDialog({ open, onClose, onSaved, employeeId: fixedEmployeeId, worksiteId: fixedWorksiteId }) {
    const [employees, setEmployees] = useState(null);
    const [worksites, setWorksites] = useState(null);
    const [types, setTypes] = useState([]);
    const [employeeIds, setEmployeeIds] = useState([]);
    const [worksiteId, setWorksiteId] = useState("");
    const [typeSel, setTypeSel] = useState("");
    const [newType, setNewType] = useState("");
    const [days, setDays] = useState([1, 2, 3, 4, 5]);
    const [start, setStart] = useState("06:00");
    const [hours, setHours] = useState("3");
    const [frequency, setFrequency] = useState({ type: "weekly" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEmployeeIds(fixedEmployeeId ? [fixedEmployeeId] : []);
        setWorksiteId(fixedWorksiteId || "");
        setTypeSel(""); setNewType(""); setDays([1, 2, 3, 4, 5]); setStart("06:00"); setHours("3");
        setFrequency({ type: "weekly" });
        Promise.all([
            api.get("/presenze/planning/employees"),
            api.get("/presenze/worksites"),
            api.get("/presenze/activity-types"),
        ]).then(([e, w, t]) => {
            setEmployees(e.data);
            setWorksites(w.data.filter((s) => s.active !== false || s.id === fixedWorksiteId));
            setTypes(t.data.types || []);
        }).catch(() => toast.error("Errore nel caricamento dei dati"));
    }, [open, fixedEmployeeId, fixedWorksiteId]);

    const hoursNum = parseFloat(String(hours).replace(",", "."));
    const end = Number.isFinite(hoursNum) && hoursNum > 0 ? endFromStart(start, hoursNum) : "—";
    const byDate = isDateDriven(frequency);
    const weekHours = Number.isFinite(hoursNum) ? hoursNum * weekInterventions(frequency, days) : 0;
    const selEmps = useMemo(() => employeeIds.map((id) => employees?.find((e) => e.id === id)).filter(Boolean), [employees, employeeIds]);
    const overContract = selEmps.filter((e) => e.contratto_ore && e.assigned_week_hours + weekHours > e.contratto_ore);
    const noTimbratura = selEmps.filter((e) => !e.timbratura_enabled);
    const activityType = typeSel === NEW_TYPE ? newType.trim() : typeSel;
    const toggleDay = (d) => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d].sort((a, b) => a - b)));

    const save = async () => {
        if (!employeeIds.length) { toast.error("Seleziona almeno un dipendente"); return; }
        if (!worksiteId) { toast.error("Seleziona un cliente/cantiere"); return; }
        if (!activityType) { toast.error("Indica la tipologia di attività"); return; }
        if (!byDate && !days.length) { toast.error("Seleziona almeno un giorno"); return; }
        if (!Number.isFinite(hoursNum) || hoursNum <= 0) { toast.error("Indica le ore di lavoro per intervento"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/presenze/assignments/quick", {
                employee_ids: employeeIds, worksite_id: worksiteId, activity_type: activityType,
                days, start, hours: hoursNum, frequency,
            });
            const n = data.count || 1;
            toast.success(`${n === 1 ? "Assegnazione creata" : `${n} assegnazioni create`}${data.created_activity ? " (nuova attività sul cantiere)" : ""}`);
            onSaved?.(data);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    const loading = !employees || !worksites;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto" data-testid="quick-assign-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-slate-800">
                        <UserPlus className="h-5 w-5 text-[#376DB3]" /> Assegna cliente/cantiere {fixedEmployeeId ? "al dipendente" : "ai dipendenti"}
                    </DialogTitle>
                    <DialogDescription>
                        Indica {fixedEmployeeId ? "cantiere" : "uno o più dipendenti (stesso turno), cantiere"}, tipologia, giorni, orario e ore: la pianificazione settimanale si aggiorna da sola.
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
                ) : (
                    <div className="space-y-4">
                        <div className={`grid gap-3 ${fixedEmployeeId ? "sm:grid-cols-2" : ""}`}>
                            <div>
                                <Label className={labelCls}>{fixedEmployeeId ? "Dipendente" : "Dipendenti *"}</Label>
                                {fixedEmployeeId ? (
                                    <div className={`${selectCls} flex items-center bg-slate-50 text-slate-700`} data-testid="qa-employee-fixed">
                                        {selEmps[0] ? `${selEmps[0].cognome} ${selEmps[0].nome}${selEmps[0].matricola ? ` (${selEmps[0].matricola})` : ""} — ${fmtH(selEmps[0].assigned_week_hours)}h assegnate${selEmps[0].contratto_ore ? ` / ${fmtH(selEmps[0].contratto_ore)}h` : ""}` : "…"}
                                    </div>
                                ) : (
                                    <div className="mt-1">
                                        <EmployeeMultiSelect employees={employees} selected={employeeIds} onChange={setEmployeeIds} testPrefix="qa-emp" maxHeight="max-h-44" />
                                        {selEmps.length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="qa-selected-chips">
                                                {selEmps.map((e) => (
                                                    <span key={e.id} className="rounded-full bg-[#376DB3]/10 px-2 py-0.5 text-[11px] font-semibold text-[#376DB3]">{e.cognome} {e.nome}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label className={labelCls}>Cliente / Cantiere *</Label>
                                <select value={worksiteId} onChange={(e) => setWorksiteId(e.target.value)} disabled={!!fixedWorksiteId}
                                    className={selectCls} data-testid="qa-worksite-select">
                                    <option value="">Seleziona…</option>
                                    {worksites.map((w) => (
                                        <option key={w.id} value={w.id}>{w.name}{w.client_name ? ` — ${w.client_name}` : ""}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <Label className={labelCls}>Tipologia attività *</Label>
                            <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                <select value={typeSel} onChange={(e) => setTypeSel(e.target.value)} className={selectCls.replace("mt-1 ", "")} data-testid="qa-type-select">
                                    <option value="">Seleziona…</option>
                                    {types.map((t) => <option key={t} value={t}>{t}</option>)}
                                    <option value={NEW_TYPE}>Altra tipologia (nuova)…</option>
                                </select>
                                {typeSel === NEW_TYPE && (
                                    <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Es. Pulizia palestre" className="h-9" data-testid="qa-type-new-input" />
                                )}
                            </div>
                        </div>

                        {!byDate && (
                            <div>
                                <Label className={labelCls}>Giorni di intervento *</Label>
                                <div className="mt-1.5"><DayPills days={days} onToggle={toggleDay} testPrefix="qa-day" /></div>
                            </div>
                        )}

                        <div className="grid gap-3 sm:grid-cols-3">
                            <div>
                                <Label className={labelCls}>Orario inizio *</Label>
                                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className={selectCls} data-testid="qa-start-input" />
                            </div>
                            <div>
                                <Label className={labelCls}>Ore per intervento *</Label>
                                <Input type="number" min={0.01} max={24} step={0.01} value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1 h-9" placeholder="es. 1,25" data-testid="qa-hours-input" />
                            </div>
                            <div>
                                <Label className={labelCls}>Fine (calcolata)</Label>
                                <div className="mt-1 flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 font-mono-numbers text-sm text-slate-700" data-testid="qa-end-preview">
                                    <Clock className="h-3.5 w-3.5 text-[#376DB3]" /> {start} → {end}
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className={labelCls}>Frequenza</Label>
                            <div className="mt-1"><FrequencyPicker value={frequency} onChange={setFrequency} testPrefix="qa-freq" /></div>
                        </div>

                        <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="qa-hours-preview">
                            {byDate ? `${freqLabel(frequency)} × ` : `${days.length} intervent${days.length === 1 ? "o" : "i"} × `}{fmtH(Number.isFinite(hoursNum) ? hoursNum : 0)} h{byDate ? "" : ` · ${freqLabel(frequency)}`} →{" "}
                            <b className="font-mono-numbers">{fmtH(weekHours)} h/settimana</b> in media{selEmps.length > 1 ? " per ciascun dipendente" : ""}
                        </div>
                        {overContract.length > 0 && (
                            <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="qa-over-contract-warning">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    {overContract.map((e) => (
                                        <div key={e.id}>Con questa assegnazione {e.cognome} {e.nome} supera le ore contrattuali settimanali ({fmtH(e.assigned_week_hours + weekHours)} h su {fmtH(e.contratto_ore)} h).</div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {noTimbratura.length > 0 && (
                            <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="qa-timbratura-warning">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                Timbratura non abilitata per {noTimbratura.map((e) => `${e.cognome} ${e.nome}`).join(", ")}: l'assegnazione vale comunque per la pianificazione. Per far timbrare, abilita da Presenze → Abilitazioni.
                            </div>
                        )}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose} data-testid="qa-cancel-btn">Annulla</Button>
                    <Button onClick={save} disabled={saving || loading} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="qa-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Assegna{employeeIds.length > 1 ? ` (${employeeIds.length})` : ""}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
